import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BannerComponent } from './banner.component';
import { BannerService } from '@core/services';
import { BannerStore } from '@shared/stores/banner.store';

/**
 * BannerContainer Component
 * Container component for managing multiple banner notifications
 *
 * @description
 * Manages the display and positioning of all active banners.
 * Renders banners from the BannerStore and handles dismiss events.
 *
 * Features:
 * - Fixed positioning at top-right of viewport
 * - Supports multiple simultaneous banners (stacked vertically)
 * - Pointer events optimization
 * - Integrates with BannerService for dismissal
 *
 * Usage:
 * Add once to AppComponent template:
 * ```html
 * <app-banner-container />
 * ```
 */
@Component({
  selector: 'app-banner-container',
  standalone: true,
  imports: [CommonModule, BannerComponent],
  template: `
    <div class="banner-container" [class.has-banners]="hasVisibleBanners()">
      @for (banner of banners(); track banner.id) {
        <app-banner [config]="banner" (onClose)="dismissBanner(banner.id)" />
      }
    </div>
  `,
  styles: [
    `
      .banner-container {
        position: fixed;
        top: 1rem;
        right: 1rem;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        pointer-events: none;
        max-width: 600px;
        width: calc(100% - 2rem);
      }

      .banner-container.has-banners {
        pointer-events: auto;
      }

      @media (max-width: 640px) {
        .banner-container {
          top: 0.5rem;
          right: 0.5rem;
          width: calc(100% - 1rem);
        }
      }
    `,
  ],
})
export class BannerContainerComponent {
  private readonly bannerService = inject(BannerService);
  private readonly bannerStore = inject(BannerStore);

  // Expose store signals
  protected readonly banners = this.bannerStore.banners;
  protected readonly hasVisibleBanners = this.bannerStore.hasVisibleBanners;

  /**
   * Dismiss a specific banner
   */
  dismissBanner(id: string): void {
    this.bannerService.dismiss(id);
  }
}
